import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@/features/admin/api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { toast } from '@/hooks/use-toast'
import { ObjectDetailsTab } from './ObjectDetailsTab'
import { FloorPlanTab } from './FloorPlanTab'
import { RecoveryInformationTab } from './RecoveryInformationTab'
import { RecoveryDocumentVideoTab } from './RecoveryDocumentVideoTab'
import { RunbookTab } from './RunbookTab'
import { ObjectPictureTab } from './ObjectPictureTab'
import { useCreateObject, useUpdateObject, useObject } from '../hooks'
import { createObjectSchema, updateObjectSchema, type ObjectFormData } from '../schema'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import type { Object as ObjectEntity } from '@/types/entities'
import { Map, FileText, Video, BookOpen, Image, List } from 'lucide-react'
import { useCan } from '@/hooks/useCan'

// Which tab owns each field, so an invalid one can be brought into view.
// Anything unlisted lives on the Details tab.
const FIELD_TAB: Record<string, string> = {
    FloorPlanImage: 'floorPlan',
    FloorPlanMarker: 'floorPlan',
    Instructions: 'recoveryInfo',
    RecoveryDocument: 'recoveryDocVideo',
    RecoveryVideo: 'recoveryDocVideo',
    MasterRunbook: 'runbook',
    RunbookTasks: 'runbook',
    ObjectImage: 'objectPicture',
}

interface ObjectFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    object: ObjectEntity | null
    objectService?: 'Object' | 'Service'
}

export function ObjectFormDialog({ open, onOpenChange, object, objectService = 'Object' }: ObjectFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!object
    const createObject = useCreateObject()
    const updateObject = useUpdateObject()
    const [activeTab, setActiveTab] = useState('details')
    const showRunbookTab = !isEditing
    
    // Fetch full object data when editing (list objects don't have all fields)
    const { data: fullObjectData, isLoading: isLoadingObject, error: objectLoadError } = useObject(
        isEditing && object?.objectID ? object.objectID : null
    )

    // When editing, populate ONLY from the full record. The row handed in from the
    // list is a thin 7-column projection (no classification, team, RPO/RTO, …), so
    // falling back to it silently produced a half-empty form that then failed to
    // validate. If the detail fetch fails we say so rather than editing a stub.
    const objectToUse = isEditing ? (fullObjectData ?? null) : object

    const form = useForm<ObjectFormData>({
        resolver: zodResolver(isEditing ? updateObjectSchema : createObjectSchema) as any,
        mode: 'onChange',
        defaultValues: {
            ObjectService: 'Object',
            ObjectID: '',
            Description: null,
            ClassificationID: null,
            ResponsibleTeam: null,
            RPO: null,
            RTO: null,
            Location: null,
            ManagementOnly: 0,
            SharedServiceOverride: null,
            BackupSolution: null,
            Datastore: 0,
            ApplicationEncryption: 1,
            Retention: 30,
            Analytics: 'Yes',
            Icon: null,
            Technology: null,
            FloorPlanImage: null,
            FloorPlanMarker: null,
            Instructions: null,
            RecoveryDocument: null,
            RecoveryVideo: null,
            MasterRunbook: null,
            RunbookTasks: [],
            ObjectImage: null,
        },
    })

    // ObjectService (Object vs Service) is user-selectable in the form (a control to the
    // right of the Object ID), so the layout/field set follows the LIVE form value rather
    // than a fixed creation-time prop — this also lets an existing object be edited into a
    // service (and vice-versa).
    const isService = form.watch('ObjectService') === 'Service'

    // If we're on the Floor Plan tab and the entity is switched to a Service (which has no
    // floor plan), fall back to Details so we don't show a blank tab body.
    useEffect(() => {
        if (isService && activeTab === 'floorPlan') setActiveTab('details')
    }, [isService, activeTab])

    // Configured default object retention (from System Settings) — pre-fills the form. The route
    // answers system.admin or objects.create (a new object is what it pre-fills); anyone else keeps
    // the fallback below rather than a 403.
    const canReadRetention = useCan('objects.create', 'system.admin')
    const { data: retentionDefaults } = useQuery({
        queryKey: ['retention-defaults'],
        queryFn: async () => {
            const res = await adminApi.getRetentionDefaults()
            return res.data.data
        },
        enabled: canReadRetention,
    })
    const defaultObjectRetention = retentionDefaults?.object ?? 30

    // Reset form when dialog opens/closes or object changes
    useEffect(() => {
        if (open) {
            // Don't reset while the full record is still loading — and don't blank
            // the form into "create" defaults if it failed to load either.
            if (isEditing && (isLoadingObject || objectLoadError)) {
                return
            }

            if (objectToUse) {
                // RPO/RTO dropdown options
                const rpoRtoOptions = ['15', '30', '60', '120', '240', '480', '1440']
                
                // Convert RPO: if it matches a dropdown option, use string; otherwise keep as number
                let rpoValue: string | number | null = null
                if (objectToUse.rpo !== null && objectToUse.rpo !== undefined) {
                    const rpoStr = objectToUse.rpo.toString()
                    rpoValue = rpoRtoOptions.includes(rpoStr) ? rpoStr : Number(objectToUse.rpo)
                }
                
                // Convert RTO: if it matches a dropdown option, use string; otherwise keep as number
                let rtoValue: string | number | null = null
                if (objectToUse.rto !== null && objectToUse.rto !== undefined) {
                    const rtoStr = objectToUse.rto.toString()
                    rtoValue = rpoRtoOptions.includes(rtoStr) ? rtoStr : Number(objectToUse.rto)
                }
                
                console.log('Resetting form with object data:', objectToUse)
                
                form.reset({
                    ObjectService: objectToUse.objectService || 'Object',
                    ObjectID: objectToUse.objectID || '',
                    Description: objectToUse.objectDescription || null,
                    ClassificationID: objectToUse.classificationId ?? null,
                    // 0 is the API's "no team" sentinel — normalise it to null so the
                    // select shows its placeholder instead of an unmatched value
                    // (matches how Location/Technology/BackupSolution are handled).
                    ResponsibleTeam: Number(objectToUse.responsibleTeam) || null,
                    RPO: rpoValue,
                    RTO: rtoValue,
                    Location: objectToUse.locationID || null,
                    ManagementOnly: objectToUse.managementOnly || 0,
                    SharedServiceOverride: objectToUse.sharedServiceOverride ?? null,
                    BackupSolution: objectToUse.backupSolution || null,
                    Datastore: objectToUse.datastore || 0,
                    ApplicationEncryption: objectToUse.applicationEncryption === '1' || objectToUse.applicationEncryption === 1 ? 1 : 0,
                    Retention: objectToUse.retention != null ? Number(objectToUse.retention) : defaultObjectRetention,
                    Analytics: objectToUse.analytics === 'Yes' || objectToUse.analytics === 'yes' ? 'Yes' : 'No',
                    Icon: objectToUse.iconsId != null && objectToUse.iconsId !== 0 ? String(objectToUse.iconsId) : null,
                    Technology: objectToUse.technology || null,
                    FloorPlanImage: null,
                    FloorPlanMarker: null,
                    Instructions: objectToUse.instructions || null,
                    RecoveryDocument: null,
                    RecoveryVideo: null,
                    MasterRunbook: objectToUse.masterRunbook || null,
                    RunbookTasks: [],
                    ObjectImage: null,
                })
            } else {
                form.reset({
                    ObjectService: objectService,
                    ObjectID: '',
                    Description: null,
                    ClassificationID: null,
                    ResponsibleTeam: null,
                    RPO: null,
                    RTO: null,
                    Location: null,
                    ManagementOnly: 0,
                    SharedServiceOverride: null,
                    BackupSolution: null,
                    Datastore: 0,
                    ApplicationEncryption: 1,
                    Retention: defaultObjectRetention,
                    Analytics: 'Yes',
                    Icon: null,
                    Technology: null,
                    FloorPlanImage: null,
                    FloorPlanMarker: null,
                    Instructions: null,
                    RecoveryDocument: null,
                    RecoveryVideo: null,
                    MasterRunbook: null,
                    RunbookTasks: [],
                    ObjectImage: null,
                })
            }
            setActiveTab('details')
        }
    }, [open, objectToUse, form, isEditing, isLoadingObject, objectLoadError])

    // A failed detail fetch used to be completely silent (the dialog just showed a
    // half-populated form built from the list row).
    useEffect(() => {
        if (open && isEditing && objectLoadError) {
            toast({
                variant: 'destructive',
                title: t('objects.loadFailed', 'Could not load this object'),
                description: t(
                    'objects.loadFailedDescription',
                    'Its details could not be fetched, so it cannot be edited safely. Close the dialog and try again.'
                ),
            })
        }
    }, [open, isEditing, objectLoadError, t])

    const handleSubmit = async (data: ObjectFormData) => {
        try {
            // Recalculate TaskOffset for all runbook tasks before submission
            let runbookTasks = data.RunbookTasks || []
            if (runbookTasks.length > 0) {
                runbookTasks = calculateRunbookTimes(runbookTasks)
            }
            
            // Transform form data to API format
            const submitData: any = {
                ObjectService: data.ObjectService || objectService,
                ObjectID: data.ObjectID,
                Description: data.Description || null,
                // LayerID is deliberately absent. It used to be echoed back
                // verbatim, which pinned the legacy column to whatever stale
                // value the row already held and stopped the API deriving it
                // from the classification. Omitting the key (rather than
                // sending null, which the API reads as "set it to 0") lets the
                // classification drive it while it still exists, and is inert
                // once the API stops writing it at all.
                ClassificationID: data.ClassificationID || null,
                ResponsibleTeam: data.ResponsibleTeam || null,
                RPO: isService ? null : (data.RPO || null),
                RTO: data.RTO || null,
                // A service may have a location too (some do, some do not); an object must.
                Location: data.Location || null,
                ManagementOnly: isService ? null : (data.ManagementOnly || 0),
                // Sent even for services: a service can be a shared service too, and
                // null here means "inherit", not "unset".
                SharedServiceOverride: data.SharedServiceOverride ?? null,
                BackupSolution: isService ? null : (data.BackupSolution || null),
                Datastore: isService ? null : (data.Datastore || 0),
                ApplicationEncryption: isService ? null : (data.ApplicationEncryption || 0),
                Retention: isService ? null : (data.Retention ?? null),
                Analytics: isService ? null : (data.Analytics || 'No'),
                Technology: isService ? null : (data.Technology || null),
                Icon: data.Icon || null,
                Instructions: data.Instructions || null,
                RecoveryDocument: data.RecoveryDocument || null,
                RecoveryVideo: data.RecoveryVideo || null,
                MasterRunbook: data.MasterRunbook || null,
                RunbookTasks: runbookTasks,
            }

            if (isEditing && objectToUse?.objectID) {
                await updateObject.mutateAsync({
                    id: objectToUse.objectID,
                    data: submitData,
                })
            } else {
                await createObject.mutateAsync(submitData as any)
            }
            onOpenChange(false)
        } catch (error: any) {
            console.error('Form submit error:', error)
            console.error('Error response:', error?.response?.data)
            console.error('Error message:', error?.message)

            // The dialog stays open on failure; without this the user got no
            // indication at all that the save had been rejected.
            const body = error?.response?.data
            toast({
                variant: 'destructive',
                title: t('objects.saveFailed', 'Could not save'),
                description:
                    body?.message ||
                    body?.error ||
                    error?.message ||
                    t('objects.saveFailedDescription', 'The server rejected the change. Please try again.'),
            })
        }
    }

    // react-hook-form silently does nothing when validation fails, which is what
    // made "Save does nothing" so hard to diagnose. Say what is wrong and go to it.
    const handleInvalid = (errors: Record<string, any>) => {
        const names = Object.keys(errors)
        if (names.length === 0) return

        setActiveTab(FIELD_TAB[names[0]] ?? 'details')

        const detail = names
            .map((n) => {
                const message = errors[n]?.message
                return message ? `${n}: ${message}` : n
            })
            .join('; ')

        toast({
            variant: 'destructive',
            title: t('objects.validationFailed', 'Some fields need attention'),
            description: detail,
        })

        // Best-effort: a field without a rendered input can't be focused, and the
        // feedback above must not be lost to an exception from the focus attempt.
        try {
            form.setFocus(names[0] as any)
        } catch {
            /* no input registered for this field */
        }
    }

    const isLoading = createObject.isPending || updateObject.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* h-[90vh], NOT max-h-[90vh]. With a max-height the dialog is sized by whatever the
                active tab happens to contain and merely capped, so switching from Object Details to
                Object Picture visibly collapsed the shell — the buttons jumped up the screen. A
                fixed height makes the tab panel below (flex-1 min-h-0 overflow-y-auto) absorb the
                difference instead, so the frame stays put and only its contents change. */}
            <DialogContent className="sm:max-w-[1400px] h-[90vh] flex flex-col">
                <DialogHeader className="shrink-0">
                    <DialogTitle>
                        {isEditing 
                            ? t('objects.editObject') 
                            : (isService ? t('objects.createService') : t('objects.createObject'))}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('objects.updateObjectInfo')
                            : (isService ? t('objects.createNewServiceDescription') : t('objects.createNewObjectDescription'))}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit, handleInvalid)} className="flex flex-col flex-1 min-h-0">
                        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col flex-1 min-h-0">
                            <TabsList
                                // shrink-0: the only flex child of the fixed-height dialog that
                                // could otherwise squash. The scrolling tab panel is what should
                                // absorb height, never the tab bar.
                                className={`shrink-0 grid w-full ${
                                    isService
                                        ? showRunbookTab
                                            ? 'grid-cols-5'
                                            : 'grid-cols-4'
                                        : showRunbookTab
                                            ? 'grid-cols-6'
                                            : 'grid-cols-5'
                                }`}
                            >
                                <TabsTrigger value="details" className="flex items-center gap-2">
                                    <BookOpen className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.objectDetails')}
                                </TabsTrigger>
                                {!isService && (
                                    <TabsTrigger value="floorPlan" className="flex items-center gap-2">
                                        <Map className="h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.floorPlan')}
                                    </TabsTrigger>
                                )}
                                <TabsTrigger value="recoveryInfo" className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.recoveryInformation')}
                                </TabsTrigger>
                                <TabsTrigger value="recoveryDocVideo" className="flex items-center gap-2">
                                    <Video className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.recoveryDocumentVideo')}
                                </TabsTrigger>
                                {showRunbookTab && (
                                    <TabsTrigger value="runbook" className="flex items-center gap-2">
                                        <List className="h-4 w-4 text-blue-600" />
                                        {t('objects.tabs.runbook')}
                                    </TabsTrigger>
                                )}
                                <TabsTrigger value="objectPicture" className="flex items-center gap-2">
                                    <Image className="h-4 w-4 text-blue-600" />
                                    {t('objects.tabs.objectPicture')}
                                </TabsTrigger>
                            </TabsList>

                            <div className="flex-1 min-h-0 overflow-y-auto mt-4 pr-1">
                                <TabsContent value="details" className="mt-0">
                                    <ObjectDetailsTab form={form} isEditing={isEditing} isService={isService} />
                                </TabsContent>

                                {!isService && (
                                    <TabsContent value="floorPlan" className="mt-0">
                                        <FloorPlanTab form={form} />
                                    </TabsContent>
                                )}

                                <TabsContent value="recoveryInfo" className="mt-0">
                                    <RecoveryInformationTab form={form} />
                                </TabsContent>

                                <TabsContent value="recoveryDocVideo" className="mt-0">
                                    <RecoveryDocumentVideoTab form={form} />
                                </TabsContent>

                                {showRunbookTab && (
                                    <TabsContent value="runbook" className="mt-0">
                                        <RunbookTab form={form} />
                                    </TabsContent>
                                )}

                                <TabsContent value="objectPicture" className="mt-0">
                                    <ObjectPictureTab form={form} />
                                </TabsContent>
                            </div>
                        </Tabs>

                        <div className="shrink-0 flex justify-end space-x-2 pt-4 mt-4 border-t bg-background">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('common.back')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? (isEditing ? t('objects.updating') : t('objects.creating'))
                                    : t('common.save')}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
